import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextField } from '../../components/ui'
import { keys } from '../../api/keys'
import { updateProperty } from '../../api/objects-write'
import type { Property } from '../../api/objects'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'

/**
 * Correcting an address after it has been entered.
 *
 * The endpoint has been there since the inventory was built; the screen never
 * offered it. A typo in a street, a floor entered on the wrong flat, a note
 * written about the neighbours — the only way to fix any of them was to delete
 * the whole object and enter it again, losing its photographs, its prices and
 * every check anybody had recorded against it.
 *
 * A field left alone is not sent. A field cleared is sent as null, which is the
 * only way to unsay a floor typed by mistake: absent and empty are different
 * requests and this is where the difference is made.
 */
export function EditPropertyForm({ property }: { readonly property: Property }) {
  const { can } = useSession()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const [fields, setFields] = useState(() => fieldsOf(property))

  function reset() {
    setFields(fieldsOf(property))
    setFailure(null)
  }

  const save = useMutation({
    mutationFn: () =>
      updateProperty(property.id, {
        address_normalized: fields.addressNormalized.trim(),
        address_exact: fields.addressExact.trim(),
        area: fields.area.trim(),
        municipality: fields.municipality.trim(),
        heating: fields.heating.trim(),
        notes: fields.notes.trim(),
        floor: fields.floor.trim() === '' ? null : Number(fields.floor.trim()),
        elevator: fields.elevator === '' ? null : fields.elevator === 'yes',
      }),
    onSuccess: () => {
      setOpen(false)
      setFailure(null)
      void client.invalidateQueries({ queryKey: keys.property(property.id) })
      void client.invalidateQueries({ queryKey: keys.anyPropertyList() })
    },
    onError: (error: unknown) => {
      setFailure(error instanceof Error ? error.message : t('build.failed'))
    },
  })

  if (!can('listings.write')) return null

  if (!open) {
    return (
      <Button
        variant="ghost"
        onClick={() => {
          reset()
          setOpen(true)
        }}
      >
        {t('objects.editAddress')}
      </Button>
    )
  }

  const emptyAddress = fields.addressNormalized.trim() === ''

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (!emptyAddress) save.mutate()
      }}
    >
      <TextField
        label={t('objects.addressNormalized')}
        value={fields.addressNormalized}
        error={emptyAddress ? t('objects.addressRequired') : undefined}
        onChange={(event) => {
          setFields({ ...fields, addressNormalized: event.target.value })
        }}
      />
      <TextField
        label={t('objects.addressExact')}
        value={fields.addressExact}
        onChange={(event) => {
          setFields({ ...fields, addressExact: event.target.value })
        }}
      />
      <div className="build-form__row">
        <TextField
          label={t('objects.area')}
          value={fields.area}
          onChange={(event) => {
            setFields({ ...fields, area: event.target.value })
          }}
        />
        <TextField
          label={t('objects.municipality')}
          value={fields.municipality}
          onChange={(event) => {
            setFields({ ...fields, municipality: event.target.value })
          }}
        />
      </div>
      <div className="build-form__row">
        <TextField
          label={t('objects.floor')}
          value={fields.floor}
          inputMode="numeric"
          onChange={(event) => {
            setFields({ ...fields, floor: event.target.value })
          }}
        />
        <label className="field">
          <span className="field__label">{t('objects.elevator')}</span>
          <select
            className="field__input"
            value={fields.elevator}
            onChange={(event) => {
              setFields({ ...fields, elevator: event.target.value })
            }}
          >
            {/* Three answers: a lift nobody asked about is not a flat without one. */}
            <option value="">{t('objects.notAsked')}</option>
            <option value="yes">{t('common.yes')}</option>
            <option value="no">{t('common.no')}</option>
          </select>
        </label>
      </div>
      <TextField
        label={t('objects.heating')}
        value={fields.heating}
        onChange={(event) => {
          setFields({ ...fields, heating: event.target.value })
        }}
      />
      <TextField
        label={t('objects.notes')}
        value={fields.notes}
        onChange={(event) => {
          setFields({ ...fields, notes: event.target.value })
        }}
      />

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={save.isPending} disabled={emptyAddress}>
          {save.isPending ? t('build.saving') : t('build.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          disabled={save.isPending}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function fieldsOf(property: Property) {
  return {
    addressNormalized: property.addressNormalized,
    addressExact: property.addressExact,
    area: property.area,
    municipality: property.municipality,
    heating: property.heating,
    notes: property.notes,
    floor: property.floor === undefined ? '' : String(property.floor),
    elevator: property.elevator === undefined ? '' : property.elevator ? 'yes' : 'no',
  }
}
